import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Col,
  Row,
  Modal,
  Form,
  Input,
  Spin,
  Button,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import { postFacturacion } from 'api/recaudacion/recibos';
import { getUsosDeCFDI, getCodigoPostalById } from 'api/catalogos';
import { RFC_PATTERN } from 'utils/patterns';
import { getRegimenFiscales } from 'api/empresas';
import { getCiudadano } from 'api/cuentaunicasir';

const ModalUsoFactura = ({
  visible,
  setVisible,
  recibosAFacturar,
  callback,
}) => {
  const entidad = useSelector(({ auth }) => auth.entidad);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uso, setUso] = useState([]);
  const [regimenFiscal, setRegimenFiscal] = useState([]);
  const { direccion } = entidad;

  const debounced = useRef(null);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
  };

  const debouncePost = (values) => {
    if (debounced.current) clearTimeout(debounced.current);

    debounced.current = setTimeout(async () => {
      const results = await postFacturacion(recibosAFacturar, values);
      if (results) {
        onCancel();
        callback(values);
      }
      setLoading(false);
    }, 500);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      debouncePost(values);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      const dataUsos = await getUsosDeCFDI();
      const _regimen = await getRegimenFiscales();
      if (entidad?.direccion && mounted) {
        const _codigoPostal = await getCodigoPostalById(direccion.codigo_postal);
        form.setFieldsValue({ codigo_postal: `${_codigoPostal.d_codigo}` });
      }
      if (mounted) {
        setUso(dataUsos);
        setRegimenFiscal(_regimen);
      }
      setLoading(false);
    };
    fetchData();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const saveControl = {
    text: 'Guardar',
    icon: <SaveOutlined />,
    onClick: () => onFinish(),
  };

  const backControl = {
    text: 'Regresar',
    icon: <ArrowLeftOutlined />,
    onClick: () => onCancel(),
  };

  const getControls = () => [backControl, saveControl];

  const required = { required: true };

  const rules = {
    required: [required],
    rfc: [
      required,
      {
        pattern: RFC_PATTERN,
        message: 'El RFC no es válido',
      },
    ],
    email: [
      {
        required,
        type: 'email',
      },
    ],
  };

  const fillFields = async () => {
    setLoading(true);
    const _pu = await getCiudadano(entidad.ciudadano_publico_en_general);
    form.setFieldsValue({
      uso: 23,
      rfc: _pu?.RFC ?? 'XAXX010101000',
      razon_social: 'PÚBLICO GENERAL',
      regimen_fiscal: 12,
      codigo_postal: _pu?.direccion?.codigo_postal?.d_codigo,
      email: _pu?.email,
    });
    setLoading(false);
  };

  return (
    <Modal
      footer={null}
      closable={false}
      open={visible}
      loading={loading}
      title={(
        <FormSubmitControls
          loading={loading}
          label="Agregar Datos de Facturación"
          controls={getControls()}
        />
      )}
    >
      <Spin tip="Cargando..." spinning={loading}>
        <Row>
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              name="folios"
              onFinish={() => onFinish()}
            >
              <Col span={24}>
                <Button
                  onClick={fillFields}
                  style={{ marginBottom: 20 }}
                >
                  Usar datos de público general
                </Button>
              </Col>
              <Col span={24} />
              <Col span={24}>
                <Form.Item
                  name="uso"
                  label="Uso de CFDI"
                  hasFeedback
                  rules={rules.required}
                >
                  <Select dataSource={uso} keyLabelRender keyProp="uso_del_cfdi" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="rfc"
                  rules={rules.rfc}
                  label="RFC"
                  hasFeedback
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="razon_social"
                  label="Razón Social"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="email"
                  label="Correo Electrónico"
                  hasFeedback
                  rules={rules.email}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="codigo_postal"
                  label="Codigo Postal"
                  hasFeedback
                  rules={rules.required}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="regimen_fiscal"
                  label="Regimen Fiscal"
                  hasFeedback
                  rules={rules.required}
                >
                  <Select dataSource={regimenFiscal} />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};

ModalUsoFactura.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  recibosAFacturar: PropTypes.arrayOf(PropTypes.number),
  callback: PropTypes.func.isRequired,
};

ModalUsoFactura.defaultProps = {
  recibosAFacturar: [],

};
export default ModalUsoFactura;
